import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Form,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  FaClock,
  FaEdit,
  FaMapMarkerAlt,
  FaPhone,
  FaPlus,
  FaTrash,
  FaUndo,
  FaUserTie,
} from "react-icons/fa";
import { Switch } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import { getUserFromLocalStorage } from "utilities/user.js";
import { usePermissions } from "../../utilities/usePermissions.js";
import { useLocationGate } from "../../components/team/Gated";
import { MY_BRANCHES_ONLY, NOT_YOUR_OUTLETS, useOutletsInScope } from "../../hooks/useVendorAccess";
import OpenHoursManager from "../../components/OpenHours/OpenHoursManager";
import ConfirmDialog, { useConfirmDialog } from "../../components/ConfirmDialog";
import BrandOutletFormModal from "../../components/BrandOutletFormModal";
import {
  apiCreateBrandOutlet,
  apiDeleteBrandOutlet,
  apiDeleteBrandOutletHours,
  apiFetchBrandDirectory,
  apiFetchBrands,
  apiFetchBrandsByOwner,
  apiGetBrandOutletHours,
  apiGetBrandOutlets,
  apiToggleBrandOutlet,
  apiUpdateBrandOutlet,
  apiUpdateBrandOutletHours,
} from "../../api/index";
import AppSelect from "../../components/ui/AppSelect";

/**
 * Brand outlet management.
 *
 * One page, two audiences, decided by role rather than by route:
 *   /admin/brand-outlet-management  (BRANDS sidebar, Lusuku staff)
 *   /admin/brand-outlets            (My Brand sidebar, brand owner and team)
 *
 * Who may do what mirrors the server contract (routes/brandRoutes.js):
 *   Lusuku staff      create and delete outlets, edit every field, hours,
 *                     activation
 *   Brand owner/team  edit contact fields (phone, email, manager), hours,
 *                     activation; location fields are read-only
 *
 * Every request goes through the authenticated API instance. Hours are read
 * and written on the OUTLET's own hours endpoints; the brand's hours are
 * never touched from here.
 */

const useStyles = makeStyles((theme) => ({
  switchBase: {
    color: theme.palette.secondary.main,
    "&$checked": {
      color: theme.palette.secondary.main,
    },
    "&$checked + $track": {
      backgroundColor: theme.palette.secondary.main,
    },
  },
  checked: {},
  track: {},
}));

const DEFAULT_TIMEZONE = "Africa/Kampala";

const hasOwnHours = (outlet) =>
  Boolean(outlet?.openHours && typeof outlet.openHours === "object" && Object.keys(outlet.openHours).length > 0);

const formatKm = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? `${n} km` : null;
};

const OutletManagementBrand = () => {
  const classes = useStyles();
  const location = useLocation();
  const confirmDialog = useConfirmDialog();
  const { can, loaded: permissionsLoaded } = usePermissions();

  // ── Who is looking ───────────────────────────────────────────────────────
  // Read once per render; only primitives derived from it feed hooks below,
  // so a fresh object each render cannot retrigger the brand fetch.
  const user = getUserFromLocalStorage()?.result || null;
  const hasUser = Boolean(user);
  const isPlatformStaff = user?.userType === "LUSUKU STAFF";
  const isSuperAdmin = isPlatformStaff && user?.user_role === "SUPERUSER";
  // Fail closed until the permission set has loaded, as BrandManagement does;
  // the server is the real gate either way.
  const staffCan = useCallback(
    (key) => isPlatformStaff && (isSuperAdmin || (permissionsLoaded && can(key))),
    [isPlatformStaff, isSuperAdmin, permissionsLoaded, can]
  );
  const canManageBrands = staffCan("brands:manage");
  const canCreateOutlets = canManageBrands || staffCan("brands:create");
  const canEditLocation = canManageBrands || staffCan("brands:update");
  const canDeleteOutlets = canManageBrands || staffCan("brands:delete");
  const canViewDirectory =
    isPlatformStaff && (isSuperAdmin || !permissionsLoaded || can("brands:read"));
  // Brand side: the owner, or a My Team helper whose parentVendorId points at
  // the owner. GET /api/brands?vendorId= resolves either to the owner's brands.
  const isBrandSide = hasUser && !isPlatformStaff;
  const ownerUserId = user?.parentVendorId || user?._id || null;
  // Vendor team permissions: on the brand side a team member needs
  // outlets:manage to edit, toggle or re-time an outlet (owner always passes).
  //
  // Per outlet (4 Oct 2026): a member limited to some branches runs the
  // switch, the hours and the contact details of their own outlets only.
  // Creating, deleting and moving an outlet stay with Lusuku staff.
  const outletAt = useLocationGate("outlets:manage");
  const brandSideAt = (outlet) => isBrandSide && outletAt(outlet?._id).allowed;
  const staffCanToggle = canManageBrands || staffCan("brands:approve") || staffCan("brands:update");
  const canEditContactAt = (outlet) => brandSideAt(outlet) || canEditLocation;
  const canToggleAt = (outlet) => brandSideAt(outlet) || staffCanToggle;
  const canManageHoursAt = (outlet) => brandSideAt(outlet) || canEditLocation;
  // The reason a brand-side control is locked, or nothing when it is not.
  const lockTitle = (outlet, allowed) => (allowed ? undefined : isBrandSide ? outletAt(outlet?._id).title : undefined);

  // ── Brands ───────────────────────────────────────────────────────────────
  const [brands, setBrands] = useState([]);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [selectedBrandId, setSelectedBrandId] = useState("");
  const selectedBrand = useMemo(
    () => brands.find((b) => String(b._id) === String(selectedBrandId)) || null,
    [brands, selectedBrandId]
  );

  const loadBrands = useCallback(async () => {
    if (!hasUser) return;
    setBrandsLoading(true);
    try {
      let response;
      if (isPlatformStaff) {
        response = canViewDirectory
          ? await apiFetchBrandDirectory({ includeInactive: true })
          : await apiFetchBrands();
      } else {
        response = await apiFetchBrandsByOwner(ownerUserId);
      }
      const list = response.data?.data || [];
      setBrands(Array.isArray(list) ? list : []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch brands");
    } finally {
      setBrandsLoading(false);
    }
  }, [hasUser, isPlatformStaff, canViewDirectory, ownerUserId]);

  useEffect(() => {
    loadBrands();
  }, [loadBrands]);

  // Land on the right brand: the one BrandManagement linked to, or the only
  // brand this account has. Staff with a long list choose from the dropdown.
  const linkedToastRef = useRef(null);
  useEffect(() => {
    if (brands.length === 0) return;
    const linkedId = location.state?.brandId ? String(location.state.brandId) : null;
    const linked = linkedId ? brands.find((b) => String(b._id) === linkedId) : null;
    if (linked) {
      setSelectedBrandId(linkedId);
      if (linkedToastRef.current !== linkedId) {
        linkedToastRef.current = linkedId;
        toast.info(`Viewing outlets for ${location.state?.brandName || linked.name}`);
      }
      return;
    }
    if (brands.length === 1) setSelectedBrandId(String(brands[0]._id));
  }, [brands, location.state]);

  // ── Outlets ──────────────────────────────────────────────────────────────
  const [outlets, setOutlets] = useState([]);
  const [outletsLoading, setOutletsLoading] = useState(false);
  // What this login runs: a member limited to some branches sees theirs only.
  const { outlets: shownOutlets, limited: outletsLimited } = useOutletsInScope(outlets);

  const loadOutlets = useCallback(async (brandId) => {
    if (!brandId) {
      setOutlets([]);
      return;
    }
    setOutletsLoading(true);
    try {
      const response = await apiGetBrandOutlets(brandId);
      const list = response.data?.data || [];
      setOutlets(Array.isArray(list) ? list : []);
    } catch (err) {
      setOutlets([]);
      toast.error(err.response?.data?.message || "Failed to load outlets");
    } finally {
      setOutletsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOutlets(selectedBrandId);
  }, [selectedBrandId, loadOutlets]);

  // ── Create / edit ────────────────────────────────────────────────────────
  const [outletForm, setOutletForm] = useState({ show: false, outlet: null });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openCreateModal = () => {
    if (!canCreateOutlets || !selectedBrand) return;
    setOutletForm({ show: true, outlet: null });
  };
  const openEditModal = (outlet) => {
    if (!canEditContactAt(outlet)) return;
    setOutletForm({ show: true, outlet });
  };
  const closeOutletForm = () => setOutletForm({ show: false, outlet: null });

  // The modal toasts the failure (it owns the axios error shape), so this only
  // reports success and refreshes; the throw is left to propagate.
  const handleSubmitOutlet = async (body) => {
    if (!selectedBrandId) return;
    setIsSubmitting(true);
    try {
      if (outletForm.outlet) {
        await apiUpdateBrandOutlet(selectedBrandId, outletForm.outlet._id, body);
        toast.success("Outlet updated");
      } else {
        await apiCreateBrandOutlet(selectedBrandId, body);
        toast.success("Outlet created");
      }
      closeOutletForm();
      await loadOutlets(selectedBrandId);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Activation ───────────────────────────────────────────────────────────
  const [toggleLoading, setToggleLoading] = useState({});

  const handleToggleActive = async (outlet) => {
    if (!canToggleAt(outlet) || !selectedBrandId) return;
    setToggleLoading((prev) => ({ ...prev, [outlet._id]: true }));
    try {
      await apiToggleBrandOutlet(selectedBrandId, outlet._id);
      toast.success(`${outlet.name} ${outlet.isActive === false ? "activated" : "deactivated"}`);
      await loadOutlets(selectedBrandId);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to change outlet status");
    } finally {
      setToggleLoading((prev) => ({ ...prev, [outlet._id]: false }));
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────
  const handleDeleteOutlet = (outlet) => {
    if (!canDeleteOutlets || !selectedBrandId) return;
    confirmDialog.ask({
      title: `Delete ${outlet.name}?`,
      body: "Customers can no longer order from this outlet and it leaves every list. Past orders keep their record of it.",
      tone: "danger",
      confirmLabel: "Delete outlet",
      busyLabel: "Deleting...",
      onConfirm: async () => {
        try {
          await apiDeleteBrandOutlet(selectedBrandId, outlet._id);
          toast.success(`${outlet.name} deleted`);
          await loadOutlets(selectedBrandId);
        } catch (err) {
          toast.error(err.response?.data?.message || "Failed to delete outlet");
        }
      },
    });
  };

  // ── Hours ────────────────────────────────────────────────────────────────
  const [hoursOutlet, setHoursOutlet] = useState(null);
  const [hoursData, setHoursData] = useState(null);
  const [hoursLoading, setHoursLoading] = useState(false);
  const [hoursSaving, setHoursSaving] = useState(false);

  const openHoursModal = async (outlet) => {
    if (!canManageHoursAt(outlet) || !selectedBrandId) return;
    setHoursOutlet(outlet);
    setHoursData(null);
    setHoursLoading(true);
    try {
      const response = await apiGetBrandOutletHours(selectedBrandId, outlet._id);
      setHoursData(response.data?.data || {});
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load outlet hours");
      setHoursOutlet(null);
    } finally {
      setHoursLoading(false);
    }
  };

  const closeHoursModal = () => {
    if (hoursSaving) return;
    setHoursOutlet(null);
    setHoursData(null);
  };

  const handleSaveHours = async (openHours) => {
    if (!hoursOutlet || !selectedBrandId) return;
    setHoursSaving(true);
    try {
      await apiUpdateBrandOutletHours(selectedBrandId, hoursOutlet._id, { openHours });
      toast.success(`Hours saved for ${hoursOutlet.name}`);
      setHoursOutlet(null);
      setHoursData(null);
      await loadOutlets(selectedBrandId);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save outlet hours");
    } finally {
      setHoursSaving(false);
    }
  };

  const handleRevertHours = () => {
    if (!hoursOutlet || !selectedBrandId) return;
    const outlet = hoursOutlet;
    confirmDialog.ask({
      title: `Revert ${outlet.name} to brand hours?`,
      body: `The outlet drops its own schedule and follows ${selectedBrand?.name || "the brand"}'s hours again.`,
      tone: "warning",
      confirmLabel: "Revert to brand hours",
      busyLabel: "Reverting...",
      onConfirm: async () => {
        try {
          await apiDeleteBrandOutletHours(selectedBrandId, outlet._id);
          toast.success(`${outlet.name} now follows the brand's hours`);
          setHoursOutlet(null);
          setHoursData(null);
          await loadOutlets(selectedBrandId);
        } catch (err) {
          toast.error(err.response?.data?.message || "Failed to revert outlet hours");
        }
      },
    });
  };

  // `effective` is the schedule in force (the brand's when inheriting). Accept
  // it either as a bare hours map or wrapped as { openHours } so a shape change
  // on the server does not blank the editor.
  const effectiveHours = hoursData?.effective?.openHours || hoursData?.effective || null;
  const editorHours = hoursData?.openHours || effectiveHours || null;
  const hoursInherited = hoursData
    ? typeof hoursData.inherited === "boolean"
      ? hoursData.inherited
      : !hasOwnHours(hoursData)
    : true;

  // ── Render ───────────────────────────────────────────────────────────────
  const showBrandPicker = isPlatformStaff || brands.length > 1;
  const headerTitle = isPlatformStaff ? "Brand Outlet Management" : "My Outlets";

  return (
    <div className="content">
      <ConfirmDialog {...confirmDialog.props} />
      <Card>
        <Card.Header>
          <Card.Title as="h4">{headerTitle}</Card.Title>
          <p className="card-category">
            {isPlatformStaff
              ? "Branches a brand fulfils from. Staff add and move outlets; brand teams keep contact details, hours and availability current."
              : "Your brand's branches. Update contact details, trading hours and availability here. Lusuku staff add outlets and change their location."}
          </p>
        </Card.Header>
        <Card.Body>
          {showBrandPicker && (
            <Form.Group className="mb-4" style={{ maxWidth: 480 }}>
              <Form.Label>
                <strong>Brand</strong>
              </Form.Label>
              <AppSelect block
                value={selectedBrandId}
                onChange={(e) => setSelectedBrandId(e.target.value)}
                size="sm"
                disabled={brandsLoading}
              >
                <option value="">{brandsLoading ? "Loading brands..." : "Choose a brand..."}</option>
                {brands.map((brand) => (
                  <option key={brand._id} value={brand._id}>
                    {brand.name}
                    {brand.isActive === false ? " (inactive)" : ""}
                  </option>
                ))}
              </AppSelect>
            </Form.Group>
          )}

          {brandsLoading && !showBrandPicker && (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          )}

          {!brandsLoading && isBrandSide && brands.length === 0 && (
            <Alert variant="warning">No brand is linked to this account yet.</Alert>
          )}

          {selectedBrand && (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div>
                  <h5 className="mb-0">Outlets for {selectedBrand.name}</h5>
                  <small className="text-muted">
                    {outletsLimited
                      ? MY_BRANCHES_ONLY
                      : `${outlets.length} outlet${outlets.length !== 1 ? "s" : ""}`}
                  </small>
                </div>
                {canCreateOutlets && (
                  <Button variant="primary" size="sm" onClick={openCreateModal}>
                    <FaPlus className="me-1" /> Add outlet
                  </Button>
                )}
              </div>

              {outletsLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : shownOutlets.length === 0 ? (
                <Alert variant="info">
                  {outletsLimited && outlets.length > 0
                    ? NOT_YOUR_OUTLETS
                    : canCreateOutlets
                      ? "No outlets yet. Add the first branch for this brand."
                      : "No outlets yet. Lusuku staff add outlets for your brand; contact support to set one up."}
                </Alert>
              ) : (
                /* The shared console table look (`.lk-table` in
                   assets/css/lusuku-overrides.css): a quiet label row that
                   never wraps, in place of the royal-blue band this had. */
                <div className="lk-table-frame">
                  <Table responsive hover className="lk-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Address</th>
                        <th>Contact</th>
                        <th>Delivery zone</th>
                        <th>Hours</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shownOutlets.map((outlet) => {
                        const ownHours = hasOwnHours(outlet);
                        const radius = formatKm(outlet.deliveryZoneRadius);
                        const overage = formatKm(outlet.deliveryOverageMaxKm);
                        return (
                          <tr key={outlet._id}>
                            <td>
                              <strong>{outlet.name}</strong>
                            </td>
                            <td>
                              <FaMapMarkerAlt className="me-2 text-muted" />
                              {outlet.address || <span className="text-muted">No address</span>}
                            </td>
                            <td>
                              {outlet.phone && (
                                <div>
                                  <FaPhone className="me-1 text-muted" style={{ fontSize: "0.7rem" }} />
                                  {outlet.phone}
                                </div>
                              )}
                              {outlet.managerName && (
                                <div>
                                  <FaUserTie className="me-1 text-muted" style={{ fontSize: "0.7rem" }} />
                                  {outlet.managerName}
                                </div>
                              )}
                              {outlet.email && (
                                <span className="lk-table-meta">{outlet.email}</span>
                              )}
                              {!outlet.phone && !outlet.managerName && !outlet.email && (
                                <span className="text-muted">Not set</span>
                              )}
                            </td>
                            <td>
                              {radius || <span className="text-muted">Not set</span>}
                              {overage && (
                                <span className="lk-table-meta">+{overage} overage</span>
                              )}
                            </td>
                            <td>
                              {ownHours ? (
                                <Badge bg="primary">Own hours</Badge>
                              ) : (
                                <Badge bg="secondary">Inherits brand</Badge>
                              )}
                              {outlet.openStatus && typeof outlet.openStatus.isOpen === "boolean" && (
                                <span className={`lk-table-meta ${outlet.openStatus.isOpen ? "text-success" : ""}`}>
                                  {outlet.openStatus.isOpen ? "Open now" : "Closed now"}
                                </span>
                              )}
                            </td>
                            <td>
                              <Switch
                                checked={outlet.isActive !== false}
                                onChange={() => handleToggleActive(outlet)}
                                disabled={!canToggleAt(outlet) || Boolean(toggleLoading[outlet._id])}
                                title={lockTitle(outlet, canToggleAt(outlet))}
                                classes={{
                                  switchBase: classes.switchBase,
                                  track: classes.track,
                                  checked: classes.checked,
                                }}
                              />
                              {toggleLoading[outlet._id] && (
                                <Spinner animation="border" size="sm" style={{ marginLeft: 8 }} />
                              )}
                            </td>
                            <td>
                              <div className="d-flex gap-2">
                                <Button
                                  variant="info"
                                  size="sm"
                                  onClick={() => openEditModal(outlet)}
                                  disabled={!canEditContactAt(outlet)}
                                  title={
                                    lockTitle(outlet, canEditContactAt(outlet)) ||
                                    (canEditLocation ? "Edit outlet" : "Edit contact details")
                                  }
                                >
                                  <FaEdit />
                                </Button>
                                <Button
                                  variant="warning"
                                  size="sm"
                                  onClick={() => openHoursModal(outlet)}
                                  disabled={!canManageHoursAt(outlet)}
                                  title={lockTitle(outlet, canManageHoursAt(outlet)) || "Trading hours"}
                                >
                                  <FaClock />
                                </Button>
                                {canDeleteOutlets && (
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => handleDeleteOutlet(outlet)}
                                    title="Delete outlet"
                                  >
                                    <FaTrash />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </>
          )}

          {!selectedBrand && !brandsLoading && showBrandPicker && (
            <Alert variant="primary" className="mt-4">
              <FaMapMarkerAlt className="me-2" />
              Select a brand to view and manage its outlets.
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Create and edit share one form; the role decides which fields it sends. */}
      <BrandOutletFormModal
        show={outletForm.show}
        onHide={closeOutletForm}
        brand={selectedBrand}
        outlet={outletForm.outlet}
        canEditLocation={canEditLocation}
        isSubmitting={isSubmitting}
        onSubmit={handleSubmitOutlet}
      />

      {/* Outlet trading hours: read and written on the outlet's own endpoints. */}
      <Modal show={Boolean(hoursOutlet)} onHide={closeHoursModal} size="xl" centered>
        <Modal.Header closeButton={!hoursSaving}>
          <Modal.Title className="d-flex align-items-center">
            <FaClock className="me-2 text-primary" />
            Trading hours: {hoursOutlet?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {hoursLoading || !hoursData ? (
            <div className="text-center py-5">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading hours...
            </div>
          ) : (
            <>
              <Alert variant={hoursInherited ? "secondary" : "info"} className="mb-3">
                <strong>{hoursInherited ? "Inherits the brand's hours" : "Own hours"}</strong>
                <p className="mb-0 mt-1" style={{ fontSize: "0.9rem" }}>
                  {hoursInherited ? (
                    <>
                      This outlet follows <strong>{selectedBrand?.name}</strong>'s schedule. Save a schedule below to give it hours of its own.
                    </>
                  ) : (
                    <>
                      This outlet keeps its own schedule instead of <strong>{selectedBrand?.name}</strong>'s. Revert to follow the brand again.
                    </>
                  )}
                </p>
                {!hoursInherited && (
                  <Button variant="outline-secondary" size="sm" className="mt-2" onClick={handleRevertHours} disabled={hoursSaving}>
                    <FaUndo className="me-1" /> Revert to brand hours
                  </Button>
                )}
              </Alert>
              <OpenHoursManager
                key={`${hoursOutlet?._id}-${hoursInherited ? "inherited" : "own"}`}
                vendorLabel="outlet"
                location={hoursOutlet?._id || null}
                openHours={editorHours}
                onSave={handleSaveHours}
                isSaving={hoursSaving}
                timezone={hoursData.timezone || selectedBrand?.timezone || DEFAULT_TIMEZONE}
                // An outlet trades in its brand's zone; the brand form owns it.
                allowTimezoneEdit={false}
                onValidationError={() => toast.error("Fix the highlighted hours before saving")}
              />
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default OutletManagementBrand;
